/**
 * Cookie disclosure for Microsoft Clarity — appended to the kernel's analytics
 * category, so the cookie banner, the settings dialog and /cookie-policy list
 * what Clarity stores.
 *
 * Names and purposes: Microsoft's cookie list
 * (learn.microsoft.com/clarity/setup-and-installation/cookie-list); lifetimes of
 * the first-party cookies read from the tracker itself (clarity.js 0.8.70 sets
 * `_clck` for 365 days, `_clsk` for 1 day). The third-party ones live on
 * clarity.ms and are governed by Microsoft.
 */

import {
  type CookieCategoryConfig,
  type CookieConfig,
  DEFAULT_COOKIE_CATEGORIES,
} from '@xhubio-saas/core-frontend/kernel'

const PROVIDER = 'Microsoft Ireland Operations Ltd.'

const clarityCookies: CookieConfig[] = [
  {
    name: '_clck',
    category: 'analytics',
    description: {
      de: 'Microsoft Clarity zeichnet Sitzungen auf und erstellt Heatmaps; alle Texte der App werden dabei maskiert. Das Cookie speichert eine zufällige Clarity-Nutzer-ID. Anbieter ist Microsoft Ireland Operations Ltd.; die Daten können in die USA übermittelt werden (EU-US Data Privacy Framework). Wird nur geladen, wenn du der Analyse-Kategorie zustimmst.',
      en: 'Microsoft Clarity records sessions and builds heatmaps; all app text is masked. This cookie stores a random Clarity user ID. Provider is Microsoft Ireland Operations Ltd.; data may be transferred to the USA (EU-US Data Privacy Framework). Loaded only if you consent to the analytics category.',
    },
    duration: '1 Jahr',
    provider: PROVIDER,
  },
  {
    name: '_clsk',
    category: 'analytics',
    description: {
      de: 'Microsoft Clarity: verbindet die Seitenaufrufe eines Besuchs zu einer Aufnahme.',
      en: 'Microsoft Clarity: links the page views of one visit into a single recording.',
    },
    duration: '1 Tag',
    provider: PROVIDER,
  },
  {
    name: 'CLID, MUID, SM, MR, ANONCHK (clarity.ms)',
    category: 'analytics',
    description: {
      de: 'Microsoft Clarity: Cookies, die Microsoft auf seiner eigenen Domain clarity.ms setzen kann, unter anderem zur Wiedererkennung über Websites mit Clarity hinweg. Den Abgleich des Werbe-Cookies MUID schalten wir ab (ad_Storage: denied).',
      en: 'Microsoft Clarity: cookies Microsoft may set on its own domain clarity.ms, among other things to recognise a browser across sites using Clarity. We switch off the sync of the MUID advertising cookie (ad_Storage: denied).',
    },
    duration: 'laut Microsoft',
    provider: PROVIDER,
  },
]

/**
 * Google Analytics 4 — this app loads gtag.js on the login/registration pages
 * (`gtag.ts`, `AuthAnalytics.tsx`). Since core-frontend 5.60.0 the kernel's
 * analytics category is empty (it lists only what the kernel itself stores),
 * so the app discloses GA itself. Text taken over from the kernel's former
 * entry (core-frontend 5.41.0); GA4 sets `_ga` and `_ga_<container id>`.
 */
const googleAnalyticsCookies: CookieConfig[] = [
  {
    name: '_ga, _ga_<Container-ID>',
    category: 'analytics',
    description: {
      de: 'Google Analytics 4 misst die Reichweite der Anmelde- und Registrierungsseiten. Anbieter und Auftragsverarbeiter ist Google Ireland Ltd.; die Daten können in die USA übermittelt werden (EU-US Data Privacy Framework). Wird nur mit Zustimmung zur Analyse-Kategorie geladen.',
      en: 'Google Analytics 4 measures reach on the sign-in and registration pages. Provider and processor is Google Ireland Ltd.; data may be transferred to the USA (EU-US Data Privacy Framework). Loaded only with consent to the analytics category.',
    },
    duration: '2 Jahre',
    provider: 'Google Ireland Ltd.',
  },
]

/** The kernel's default categories, with Google Analytics and Clarity added to `analytics`. */
export const cookieCategories: CookieCategoryConfig[] = DEFAULT_COOKIE_CATEGORIES.map((category) =>
  category.id === 'analytics'
    ? { ...category, cookies: [...category.cookies, ...googleAnalyticsCookies, ...clarityCookies] }
    : category
)
